/**
 * The last net before a credential reaches a screen, a chat or a log
 * (PRE-3292 / PRE-3293).
 *
 * `cf_integration_flow.error_detail` is written from `CloudflareFailure.message`,
 * and that message has carried both the Cloudflare API token and the customer's
 * own PRERENDER_TOKEN — the latter because the deployment puts it in a Worker
 * secret, so Cloudflare can quote it back inside an error. The source-side fix
 * (PRE-3276) keeps them out of the message in the first place. This is the
 * second lock: what is rendered here, and what F5 sends to a LangGraph thread,
 * must not depend on one upstream fix staying correct.
 *
 * Over-redaction is the cheap failure here and under-redaction is a breach, so
 * the rules lean toward redacting.
 */

export const REDACTED = '[redacted]';

/**
 * Cloudflare's prefixed tokens (`cfut_`, `cft_`). The prefix makes these
 * unambiguous, so the body needs no entropy test.
 */
const CF_PREFIXED_TOKEN = /cf[a-z]{0,3}_[A-Za-z0-9_-]{20,}/g;

/**
 * The unprefixed form Cloudflare issues from its dashboard today: 40 characters
 * of base62 with `-` and `_`, and nothing to key on. Length alone would eat zone
 * ids, request ids and Worker names, so a run is only redacted when it also
 * mixes digits with both letter cases.
 *
 * `prerender-worker-example-com-route` is 34 characters and survives this; a
 * real token does not.
 */
const OPAQUE_RUN = /[A-Za-z0-9_-]{32,}/g;

const looksOpaque = (run: string): boolean =>
  /[0-9]/.test(run) && /[a-z]/.test(run) && /[A-Z]/.test(run);

/**
 * Below this length an exact match is more likely to be a coincidence in the
 * prose than the secret, and redacting a common word makes the text unreadable
 * for no gain.
 */
const MIN_KNOWN_SECRET_LENGTH = 8;

/**
 * Removes every credential this module can recognise.
 *
 * Known residual gap, stated rather than hidden: an unprefixed Cloudflare token
 * that happens to be all one letter case is not matched by shape, and a secret
 * no caller passes in is not matched by value.
 *
 * @param knownSecrets values the caller is already holding, removed by exact
 * match. The customer's Prerender token belongs here: it is 20 characters of
 * base62 and may be all one case, so no shape rule can find it without also
 * eating ordinary words.
 */
export function redactCfSecrets(
  value: string,
  knownSecrets: readonly string[] = [],
): string {
  const withoutKnown = knownSecrets
    .filter(
      (secret) =>
        typeof secret === 'string' && secret.length >= MIN_KNOWN_SECRET_LENGTH,
    )
    .reduce((text, secret) => text.split(secret).join(REDACTED), value);

  // `String.replace` with a `/g` expression resets `lastIndex` itself, so these
  // module-level expressions are safe to share across calls. `.test` would not
  // be — it carries `lastIndex` between calls and would answer `false` on every
  // second call.
  return withoutKnown
    .replace(CF_PREFIXED_TOKEN, REDACTED)
    .replace(OPAQUE_RUN, (run) => (looksOpaque(run) ? REDACTED : run));
}
